import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, siteConfig } from '@/mocks/navigation';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return <Navigate to="/" replace />;
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteConfig.name}`,
    description: category.description,
    url: `${siteConfig.url}/category/${slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} | ${siteConfig.name}`}
        description={category.description}
        keywords={`${category.name},夜间导航,夜色导航,${slug}`}
        canonicalUrl={`${siteConfig.url}/category/${slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-50 text-foreground-500">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />

            {/* Category Header */}
            <div className="py-10 md:py-16">
              <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-accent-500 rounded-xl">
                      <i className={`${category.icon} text-background-950 text-2xl`}></i>
                    </div>
                    <div>
                      <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                        {category.name}
                      </h1>
                      <p className="text-foreground-400 text-sm mt-1">{category.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-foreground-400 text-sm md:text-base leading-relaxed max-w-2xl mb-6">
                    {category.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {category.features.map((feat) => (
                      <span
                        key={feat}
                        className="inline-flex items-center gap-1.5 bg-accent-500/10 text-accent-600 text-xs font-medium px-3 py-1.5 rounded-full"
                      >
                        <i className="ri-check-line text-xs"></i>
                        {feat}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="w-full lg:w-[380px] flex-shrink-0">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-56 object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Tabs: Category Switch */}
            <div className="mb-10">
              <div className="inline-flex bg-background-100 border border-background-200/50 rounded-full px-1 py-1">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      c.slug === slug
                        ? 'bg-accent-500 text-background-950'
                        : 'text-foreground-400 hover:text-foreground-600'
                    }`}
                  >
                    <i className={`${c.icon} text-sm`}></i>
                    {c.name.replace('导航', '').replace('夜间', '')}
                  </Link>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <section className="pb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关指南与文章
              </h2>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group flex flex-col sm:flex-row bg-background-100 border border-background-200/50 rounded-xl overflow-hidden transition-all duration-300 hover:border-accent-500/20 cursor-pointer"
                    >
                      <div className="w-full sm:w-48 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 p-5">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-foreground-400 text-xs">{article.date}</span>
                          <span className="text-foreground-400 text-xs">{article.readTime}</span>
                        </div>
                        <h3 className="text-foreground-800 font-semibold text-sm md:text-base mb-2 group-hover:text-accent-500 transition-colors duration-200">
                          {article.title}
                        </h3>
                        <p className="text-foreground-400 text-xs leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-foreground-400">
                  <i className="ri-file-list-3-line text-4xl mb-4 block"></i>
                  <p className="text-sm">暂无相关文章，敬请期待</p>
                </div>
              )}
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}